import { Box, Divider, useToast } from "@chakra-ui/react";
import Form from "@rjsf/chakra-ui";
import { ErrorSchemaBuilder, GenericObjectType, RJSFSchema } from "@rjsf/utils";
import { isEqual } from "lodash";
import { useMemo } from "react";

import { RanaHeading } from "@nens/ranalib";
import { RanaErrors } from "@nens/ranalib";
import { Errors, getErrorArray } from "@nens/ranalib";
import { objectEntries } from "@nens/shared-utils";

import { JobStatusOrCreating } from "@/types/job-status-or-creating";
import { isTestEnvironment } from "@/utils/is-test-environment";

import { useProcessRunForm } from "./hooks/use-process-run-form";
import { TEMPLATES } from "./process-run-form-templates";
import { FIELDS } from "./utils/fields-const";
import { customValidator } from "./utils/form-validator";
import { WIDGETS } from "./utils/widget-const";

export interface FormContext {
  allFormData: GenericObjectType;
  processId: string;
}

export interface ProcessRunFormProps<T extends GenericObjectType> {
  title?: string;
  formData: T;
  setFormData: (formData: T) => void;
  schema: RJSFSchema | undefined;
  status: JobStatusOrCreating;
  isOutput?: boolean;
  errors?: NonNullable<Errors>[string];
  processId: string;
}

// This warning is thrown when a read-only form is changed. This can happen in a couple of ways:
// 1. A default has not been set in use-process-run-form-collection and is set by RSJF
// 2. A custom read-only field does not have proper handling of said read-only state and calls onChange when it shouldn't
// 1 isn't a big deal but could be a hint to improper state management, 2 is bad for UI/UX 99% of the time.
// So both cases should be investigated and fixed. This warning is only shown in test environments to avoid annoying users with warnings they can't do anything about.
const useWarnOnReadOnly = () => {
  const toast = useToast();

  return () => {
    if (isTestEnvironment()) {
      toast({
        title: "Form is read-only",
        description:
          "onChange should not be called when the form is read-only. Please create a ticket with what you did to get this message. (This message is only shown in test environments)",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    } else {
      console.warn(
        "Form is read-only, onChange should not be called. Find the call site and fix it."
      );
    }
  };
};

export function ProcessRunForm<T extends GenericObjectType>(props: ProcessRunFormProps<T>) {
  const warnOnReadOnly = useWarnOnReadOnly();
  const { setFormData, formData, status, schema, title, errors, processId } = props;
  const { uiSchema } = useProcessRunForm(props);

  const errorsIsString = typeof errors === "string";

  const extraErrors = useMemo(() => {
    if (!errors || errorsIsString) return undefined;

    const builder = new ErrorSchemaBuilder();

    objectEntries(errors).forEach(([key, error]) => {
      builder.addErrors(typeof error === "string" ? error : getErrorArray(error), key + "");
    });

    return builder.ErrorSchema;
  }, [errors, errorsIsString]);

  if (!schema || !schema.properties || !Object.keys(schema.properties).length) return null;

  const isReadOnly = status !== undefined;
  const formContext: FormContext = { allFormData: formData, processId };

  return (
    <Box mt={6}>
      {title && (
        <>
          <Divider mb={2} />
          <RanaHeading type="h4" mb={4}>
            {title}
          </RanaHeading>
        </>
      )}

      <Form
        readonly={isReadOnly}
        schema={schema}
        validator={customValidator}
        onChange={({ formData: newFormData }) => {
          setFormData(newFormData);

          if (isReadOnly && !isEqual(newFormData, formData)) {
            warnOnReadOnly();

            console.log("For Schema:", schema);
            console.log("New form data:", newFormData);
            console.log("Current form data:", formData);
          }
        }}
        formContext={formContext}
        uiSchema={{
          ...uiSchema,
          "ui:title": "", // This hides the schema title
        }}
        fields={FIELDS}
        formData={formData}
        templates={TEMPLATES}
        extraErrors={extraErrors}
        widgets={WIDGETS}
        // We already have our own required validation so remove those from the error list
        transformErrors={(errors) => errors.filter(({ name }) => name !== "required")}
        liveValidate
        showErrorList={false}
      />
      {errorsIsString && <RanaErrors mt={4} errors={errors} />}
    </Box>
  );
}
